import { apiService } from "../services/api";
import { getParentIdFromFormData } from "../state/utils/configUtils";

/** Lookups that filter by service_details.id (inspection parent), not jobcard id. */
export const INVOICE_SERVICE_PARENT_LOOKUPS = new Set([
  "invoice_items",
  "invoice_items_pending",
  "invoice_labour",
  "invoice_miscellaneous",
]);

/**
 * Invoice submodules need service_details.id as parentid (same as inspection workflow).
 * Dropdown may store jobcard id in parentid while selectedData.id is the service id.
 */
export const resolveInvoiceLookupParentId = (
  formData: Record<string, any>,
  parentField?: string
): string | number | null => {
  if (!formData) return null;

  const field = parentField || "parentid";
  const selected =
    formData[`${field}_selectedData`] ??
    formData.parentid1_selectedData ??
    formData.parentid_selectedData ??
    formData.parent_id_selectedData;

  if (selected && typeof selected === "object") {
    const serviceId =
      selected.parent_id ??
      selected.service_details_id ??
      selected.inspection_id ??
      selected.vehicle_in_id;
    if (serviceId !== undefined && serviceId !== null && serviceId !== "") {
      return serviceId;
    }
    if (selected.id !== undefined && selected.id !== null && selected.id !== "") {
      return selected.id;
    }
  }

  const explicit =
    formData.parent_id ?? formData[field] ?? formData.parentid;
  if (explicit !== undefined && explicit !== null && explicit !== "") {
    return explicit;
  }

  return getParentIdFromFormData(formData, field);
};

const extractLookupRows = (response: any): any[] => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (response.success && Array.isArray(response.data)) return response.data;
  if (Array.isArray(response.data)) return response.data;
  return [];
};

const fetchUtilityLookup = async (
  lookupName: string,
  parentId: string
): Promise<any[]> => {
  try {
    const response = await apiService.post<any>(
      `/pabws/utility/${lookupName}`,
      { parentid: parentId, lookuptype: "" },
      { includeAuthToken: true, errorHandling: "return-empty" }
    );
    return extractLookupRows(response);
  } catch {
    return [];
  }
};

type SpareLookupByCode = { unit: number; line: number; rate: number };

const resolveSpareRate = (row: Record<string, any>): number => {
  const rate = Number(row.rate ?? row.txt_rate ?? NaN);
  return Number.isFinite(rate) ? rate : 0;
};

/**
 * Parts-issue rows often store amount but not rate (parts issue screen omits rate).
 * Jobcard / estimation spare lookups hold the selling rate and discounts — merge by part code.
 */
export const enrichInvoiceSpareRowsWithDiscounts = async (
  rows: Record<string, any>[],
  parentId: string | number
): Promise<Record<string, any>[]> => {
  if (!rows.length || parentId === "" || parentId == null) return rows;

  const parentStr = String(parentId);
  const [jobcardSpare, estSpare] = await Promise.all([
    fetchUtilityLookup("jobcard_Spareparts", parentStr),
    fetchUtilityLookup("estimation_Spareparts", parentStr),
  ]);

  const byCode = new Map<string, SpareLookupByCode>();
  const indexRows = (list: any[]) => {
    for (const r of list) {
      const code = r.codeid ?? r.drp_code;
      if (code === undefined || code === null || code === "") continue;
      const key = String(code);
      const unit = Number(r.unit_discount ?? 0);
      const qty = Number(r.qty ?? r.req_qty ?? 1);
      const line = Number(
        r.line_discount ?? (Number.isFinite(unit) ? unit * qty : 0)
      );
      const rate = Number(r.rate ?? 0);
      const prev = byCode.get(key);
      const next: SpareLookupByCode = {
        unit: prev && prev.unit > 0 && unit === 0 ? prev.unit : unit,
        line: prev && prev.line > 0 && line === 0 ? prev.line : line,
        rate: rate > 0 ? rate : prev?.rate ?? 0,
      };
      if (
        !prev ||
        (unit > 0 && prev.unit === 0) ||
        (rate > 0 && prev.rate === 0)
      ) {
        byCode.set(key, next);
      }
    }
  };
  indexRows(estSpare);
  indexRows(jobcardSpare);

  return rows.map((row) => {
    const code = row.drp_code ?? row.codeid;
    const hit = code != null ? byCode.get(String(code)) : undefined;

    const existingUnit = Number(row.unit_discount ?? row.txt_unit_discount ?? NaN);
    const needsDiscount =
      !Number.isFinite(existingUnit) || existingUnit === 0;
    const needsRate = resolveSpareRate(row) === 0;

    if (!hit || (!needsDiscount && !needsRate)) {
      return row;
    }

    const qty = Number(row.txt_qty ?? row.qty ?? 1);
    const updates: Record<string, any> = { ...row };

    if (needsRate && hit.rate > 0) {
      updates.rate = hit.rate;
      updates.txt_rate = hit.rate;
    }

    if (needsDiscount && (hit.unit > 0 || hit.line > 0)) {
      const unit = hit.unit;
      const line = hit.line > 0 ? hit.line : unit * qty;
      updates.unit_discount = unit;
      updates.txt_unit_discount = unit;
      updates.line_discount = line;
      updates.txt_line_discount = line;
    }

    return updates;
  });
};
